import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Pin come divIcon inline: nessun asset esterno → nessun problema di path col bundler
// (i marker PNG di default di Leaflet si rompono con webpack/craco).
const pin = (bg, glyph) =>
  L.divIcon({
    className: 'points-map-pin',
    html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;background:${bg};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;"><span style="transform:rotate(45deg);font-size:14px;line-height:1;">${glyph}</span></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -26],
  });

const ORIGIN_ICON = pin('#2563eb', '📍');
const POINT_ICON = pin('#dc2626', '📦');
const SELECTED_ICON = pin('#16a34a', '✓');

const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);

const fmtDistance = (m) => {
  if (!isNum(m)) return '';
  return m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1).replace('.', ',')} km`;
};

// Adatta la vista a origine + punti ogni volta che cambiano.
function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }
  }, [bounds, map]);
  return null;
}

/**
 * Mappa interattiva dei punti di ritiro/consegna (locker InPost / Punti Poste).
 * Tile OpenStreetMap (nessuna chiave nel browser). I punti senza lat/lon vengono ignorati.
 *
 * props: points[] ({code,name,address,hours,distance,lat,lon}), origin ({lat,lng}|null),
 *        selectedCode, onSelect(point)
 */
const PointsMap = ({ points = [], origin = null, selectedCode = '', onSelect }) => {
  const geoPoints = useMemo(
    () => points.filter((p) => isNum(p.lat) && isNum(p.lon)),
    [points]
  );

  const center = useMemo(() => {
    if (origin && isNum(origin.lat) && isNum(origin.lng)) return [origin.lat, origin.lng];
    if (geoPoints.length) return [geoPoints[0].lat, geoPoints[0].lon];
    return null;
  }, [origin, geoPoints]);

  const bounds = useMemo(() => {
    const b = geoPoints.map((p) => [p.lat, p.lon]);
    if (origin && isNum(origin.lat) && isNum(origin.lng)) b.push([origin.lat, origin.lng]);
    return b;
  }, [geoPoints, origin]);

  // Niente coordinate da mostrare: lascia che la UI mostri solo la lista.
  if (!center) return null;

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200" data-testid="points-map">
      <MapContainer center={center} zoom={13} scrollWheelZoom={false} style={{ height: 260, width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds bounds={bounds} />
        {origin && isNum(origin.lat) && isNum(origin.lng) && (
          <Marker position={[origin.lat, origin.lng]} icon={ORIGIN_ICON}>
            <Popup>Indirizzo del destinatario</Popup>
          </Marker>
        )}
        {geoPoints.map((p, i) => {
          const selected = selectedCode && p.code === selectedCode;
          return (
            <Marker
              key={p.code || i}
              position={[p.lat, p.lon]}
              icon={selected ? SELECTED_ICON : POINT_ICON}
            >
              <Popup>
                <div className="text-sm">
                  <div className="font-medium text-slate-800">{p.code}{p.name ? ` · ${p.name}` : ''}</div>
                  {p.address && <div className="text-xs text-slate-500 mt-0.5">{p.address}</div>}
                  {p.hours && <div className="text-xs text-slate-500">{p.hours}</div>}
                  {isNum(p.distance) && (
                    <div className="text-xs text-slate-400 mt-0.5">a {fmtDistance(p.distance)}</div>
                  )}
                  {onSelect && (
                    <button
                      type="button"
                      className="mt-2 px-2 py-1 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
                      onClick={() => onSelect(p)}
                      data-testid={`map-select-${i}`}
                    >
                      Seleziona questo punto
                    </button>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export { fmtDistance };
export default PointsMap;
